import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import English from '../../icons/img/English.svg';
import German from '../../icons/img/German1.svg';
import Italian from '../../icons/img/ItalianFlag.svg';
import France from '../../icons/img/France.svg';
import Polish from '../../icons/img/Polish.svg';
import Spanish from '../../icons/img/Spanish.svg';
import Portuguese from '../../icons/img/Portuguese.svg';
import theme from 'themes';
import ChevronUp from 'icons/indexed-icons/ChevronUp';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import ChevronRight from 'icons/indexed-icons/ChevronRight';

interface Option {
  value: string;
  label: string;
  flag: string;
}

type DefaultLangType = Option | null | undefined;

const DropdownContainer = styled.div`
  display: flex;
  gap: 15px;
`;
const InnerDropdownContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const PopupContainer = styled.ul`
  position: absolute;
  top: 100%;
  background-color: white;
  min-width: 150px;
  padding: 10px;
  list-style: none;
  margin: 0px;
  border: 1px solid #F2F4F7;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  @media (max-width: 540px) {
    right: 101%;
    top: 55%;
  }
}
`;
const PopupInnerContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${theme.colors.textLightBlack};
`;

const DropdownMenuMobile = styled.div`
  display: flex;
  gap: 15px;
  cursor: pointer;
  align-items: center;
  @media (min-width: 541px) {
    display: none;
  }
  .icon path {
    stroke-width: 2;
  }
`;

const DropdownMenuLargeScreen = styled.div`
  display: flex;
  gap: 10px;
  cursor: pointer;
  align-items: center;
  @media (max-width: 540px) {
    display: none;
  }
  .icon path {
    stroke-width: 2;
  }
`;

const FlagImage = styled.img`
  width: 35px;
`;

const MenuImage = styled.img`
  width: 20px;
`;
const MenuItemText = styled.span`
  font-size: 14px;
  line-height: 16px;
`;

const LanguageCode = styled.span`
  color: ${theme.colors.white};
  font-size: 14px;
  line-height: 16px;
`;

const ActiveIcon = styled.div`
  padding: 1px 4px 2px;
  background: ${theme.colors.white};
  border-radius: 3px;
`;

const options = [
  { value: 'en', label: 'English : English', flag: English },
  { value: 'it', label: 'Italian : Italia', flag: Italian },
  { value: 'de', label: 'German : Deutsch', flag: German },
  { value: 'fr', label: 'France : Français', flag: France },
  { value: 'pl', label: 'Polish : Pologne', flag: Polish },
  { value: 'es', label: 'Spanish : Español', flag: Spanish },
  { value: 'pt', label: 'Portuguese : Português', flag: Portuguese }
];

const DropdownSelect: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation(['common']);

  const defaultLang = options.find((element: Option) => {
    if (i18n.language === element.value) {
      return element;
    } else return null;
  });

  // State for managing the selected option
  const [selectedOption, setSelectedOption] = useState<DefaultLangType>(defaultLang);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  // Function to toggle the dropdown visibility
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Function to handle option selection
  const handleOptionSelect = (option: Option): void => {
    const language = typeof option.value === 'string' ? option.value : '';
    setSelectedOption(option);
    setIsOpen(false);
    i18n.changeLanguage(language);
  };

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownMenuLargeScreen onClick={toggleDropdown}>
        <InnerDropdownContainer>
          {selectedOption ? (
            <>
              <LanguageCode>{selectedOption.value.toUpperCase()}</LanguageCode>
              <FlagImage src={selectedOption.flag} />
            </>
          ) : (
            <>
              {options[0].label === 'English : English' && (
                <>
                  <LanguageCode>{options[0].value.toUpperCase()}</LanguageCode>
                  <FlagImage src={options[0].flag} />
                </>
              )}
            </>
          )}
        </InnerDropdownContainer>
        <ActiveIcon style={{ backgroundColor: isOpen ? theme.colors.white : 'transparent' }}>
          {isOpen ? (
            <ChevronUp width="14px" />
          ) : (
            <ChevronDown width="14px" color={theme.colors.lightGrey10} />
          )}
        </ActiveIcon>
      </DropdownMenuLargeScreen>
      <DropdownMenuMobile onClick={toggleDropdown}>
        <InnerDropdownContainer>
          {selectedOption ? (
            <>
              <FlagImage src={selectedOption.flag} />
              <LanguageCode>{selectedOption.label.split(':')[0]}</LanguageCode>
            </>
          ) : (
            <>
              {options[0].label === 'English : English' && (
                <>
                  <FlagImage src={options[0].flag} />
                  <LanguageCode>{options[0].label.split(':')[0]}</LanguageCode>
                </>
              )}
            </>
          )}
        </InnerDropdownContainer>
        <ChevronRight />
      </DropdownMenuMobile>
      {isOpen && (
        <PopupContainer>
          {options.map((option) => (
            <li
              key={option.value}
              style={{
                padding: 5,
                cursor: 'pointer'
              }}
              onClick={() => handleOptionSelect(option)}
            >
              <PopupInnerContainer>
                <MenuImage src={option.flag} />
                <MenuItemText> {option.label}</MenuItemText>
              </PopupInnerContainer>
            </li>
          ))}
        </PopupContainer>
      )}
    </DropdownContainer>
  );
};

export default DropdownSelect;
